// packages/web-ui/src/panels/views/PartNode.tsx
//
// W4-S1 — Lifted from `tools/view-renderer/reactflow-app/src/BlockNode.jsx`.
// `data.isContainer` branches between a header-only frame (children render
// inside via React Flow `parentId`) and a leaf with attributes + ports.
//
// Changeset-preview support: `data.changeKind` ('added' | 'updated' |
// 'removed' | 'unchanged') overrides border/fill so the user can see at
// a glance which entities a proposed changeset is adding / modifying /
// removing. The same tag may appear on a port row inside `portsIn` /
// `portsOut` to color-code individual ports against an unchanged
// container.

import type { CSSProperties, JSX, ComponentType } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import {
  Antenna,
  Battery,
  Box,
  Camera,
  Cog,
  Cpu,
  Fan,
  Gauge,
  Network,
  Radar,
  Radio,
  Shield,
  Thermometer,
  Zap,
  type LucideProps,
} from 'lucide-react';
import type { PartNodeData, PortRef } from './layout-builder.js';

const HEADER_H = 50;
const ATTR_H = 14;
const PORT_GAP = 18;
const PORT_TOP = 8;
const LEAF_W = 260;

/**
 * Deterministic keyword → icon map used to give each leaf component a small
 * monochrome glyph (the polished-architecture look — every component card
 * carries an icon, like the reference diagram). Matching is a pure keyword
 * scan over the lower-cased component name then its SysML kind; the first hit
 * wins and the table is walked in array order, so the choice is stable across
 * renders. Falls back to a neutral `Box` so a card is never icon-less.
 *
 * Icons are presentation-only and carry NO semantic weight — they are a
 * comprehension aid layered on top of the authoritative name + kind text.
 */
const ICON_RULES: ReadonlyArray<[RegExp, ComponentType<LucideProps>]> = [
  [/batter|cell|accumulat/i, Battery],
  [/inverter|converter|dcdc|dc-dc|charger|rectif|power\b|hv\b|lv\b|bus/i, Zap],
  [/bms|ecu|vcu|controller|compute|soc|processor|board|gate\s?driver/i, Cpu],
  [/lidar|radar|ultrason/i, Radar],
  [/cam(era)?\b|vision|image/i, Camera],
  [/pump|fan|blower/i, Fan],
  [/radiator|thermal|coolant|temperat|heat/i, Thermometer],
  [/switch|ethernet|network|gateway|router|hub/i, Network],
  [/safety|guard|protect|monitor/i, Shield],
  [/v2x|antenn|comm|telemetr|wireless|radio/i, Antenna],
  [/motor|drive|gear|shaft|steer|brak|axle|wheel|mech/i, Cog],
  [/sensor|fusion|detect|gauge|meter|prob/i, Gauge],
  [/transceiv|signal|data/i, Radio],
];

function iconForLeaf(name: string | undefined, kind: string): ComponentType<LucideProps> {
  const haystack = `${name ?? ''} ${kind}`;
  for (const [re, Icon] of ICON_RULES) {
    if (re.test(haystack)) return Icon;
  }
  return Box;
}

/**
 * Small accent diamond drawn before a container's title — the SysML-flavoured
 * "block" marker the reference diagram uses for subsystem boxes. A rotated
 * square keeps it crisp at any zoom without an extra icon import.
 */
function AccentDiamond({ color }: { color: string }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: 9,
        height: 9,
        flexShrink: 0,
        background: color,
        transform: 'rotate(45deg)',
        borderRadius: 1.5,
      }}
    />
  );
}

/** Diff-overlay tag — see `changeset-preview-synthesizer.ts`. */
export type PartNodeChangeKind = 'added' | 'updated' | 'removed' | 'unchanged';

/**
 * Per-changeKind color palette. Tailwind-equivalent literal hex so the
 * inline style props don't require a Tailwind safelist round-trip.
 */
export interface ChangeKindStyle {
  border: string;
  fill: string;
  textDecoration?: 'line-through' | undefined;
  opacity?: number | undefined;
}

export const CHANGE_KIND_STYLES: Record<PartNodeChangeKind, ChangeKindStyle> = {
  added: {
    border: '#10B981', // emerald-500
    fill: '#ECFDF5', // emerald-50
  },
  updated: {
    border: '#F59E0B', // amber-500
    fill: '#FFFBEB', // amber-50
  },
  removed: {
    border: '#F43F5E', // rose-500
    fill: '#FFF1F2', // rose-50
    textDecoration: 'line-through',
    opacity: 0.6,
  },
  unchanged: {
    border: 'var(--tessa-border)',
    fill: 'var(--tessa-bg)',
  },
};

function resolveChangeKind(data: PartNodeData): PartNodeChangeKind {
  const ck = (data as { changeKind?: unknown }).changeKind;
  if (
    ck === 'added' ||
    ck === 'updated' ||
    ck === 'removed' ||
    ck === 'unchanged'
  ) {
    return ck;
  }
  return 'unchanged';
}

/**
 * DLA emphasis overlay — the per-node focus marker stamped onto
 * `data.emphasisKind` by `buildReactFlowGraph`. `emphasize` ⇒ an accent ring
 * (indigo, readable on light + dark), `deemphasize` ⇒ a dim. Presentation-only;
 * orthogonal to the changeset `changeKind` diff overlay (the two never co-occur
 * in practice). Style spread LAST onto a body's root so it wins over the neutral
 * boxShadow/opacity but the changeset diff (also applied) stays visible.
 */
type EmphasisKind = 'emphasize' | 'deemphasize';
function resolveEmphasisKind(data: PartNodeData): EmphasisKind | undefined {
  const e = (data as { emphasisKind?: unknown }).emphasisKind;
  return e === 'emphasize' || e === 'deemphasize' ? e : undefined;
}
const DLA_EMPHASIS_RING =
  '0 0 0 3px rgba(99, 102, 241, 0.85), 0 6px 16px rgba(15, 23, 42, 0.14)';
function emphasisStyle(data: PartNodeData): CSSProperties {
  const e = resolveEmphasisKind(data);
  if (e === 'emphasize') return { boxShadow: DLA_EMPHASIS_RING, zIndex: 4 };
  if (e === 'deemphasize') return { opacity: 0.4 };
  return {};
}

function portChangeKindOf(port: PortRef): PartNodeChangeKind {
  const ck = (port as { changeKind?: unknown }).changeKind;
  if (
    ck === 'added' ||
    ck === 'updated' ||
    ck === 'removed' ||
    ck === 'unchanged'
  ) {
    return ck;
  }
  return 'unchanged';
}

function changeIndexOf(source: unknown): number | undefined {
  if (typeof source !== 'object' || source === null) return undefined;
  const ci = (source as { changeIndex?: unknown }).changeIndex;
  return typeof ci === 'number' && Number.isFinite(ci) && ci >= 1
    ? Math.floor(ci)
    : undefined;
}

/**
 * Filled circular badge with the 1-based changeset ordinal. The colour
 * mirrors the change-kind palette (emerald / amber / rose / muted-taupe
 * for unchanged) and the white numeral gives the badge a high-contrast,
 * easy-to-read look at the small sizes the SPA renders for chat-inline
 * previews. Keeping the badge as a 22 px circle with `pointer-events:
 * none` so it never blocks React Flow interactions on the underlying
 * node.
 */
function ChangeIndexBadge({
  index,
  changeKind,
  testId,
}: {
  index: number;
  changeKind: PartNodeChangeKind;
  testId: string;
}): JSX.Element {
  const palette: Record<PartNodeChangeKind, { bg: string; fg: string }> = {
    added: { bg: '#10B981', fg: '#FFFFFF' },
    updated: { bg: '#F59E0B', fg: '#FFFFFF' },
    removed: { bg: '#F43F5E', fg: '#FFFFFF' },
    unchanged: { bg: '#6B6760', fg: '#FFFFFF' },
  };
  const { bg, fg } = palette[changeKind];
  return (
    <span
      data-testid={testId}
      data-change-index={index}
      aria-label={`Change ${index}`}
      style={{
        position: 'absolute',
        top: -10,
        right: -10,
        zIndex: 10,
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: bg,
        color: fg,
        fontSize: 11,
        fontWeight: 700,
        lineHeight: '22px',
        textAlign: 'center',
        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
        pointerEvents: 'none',
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        userSelect: 'none',
      }}
    >
      {index}
    </span>
  );
}

/**
 * Compact inline numbered chip rendered next to a port label inside the
 * block diagram. Visually smaller than `<ChangeIndexBadge>` so it can sit
 * inside a single port-row line without breaking the layout.
 */
function PortChangeIndexChip({
  index,
  changeKind,
  testId,
}: {
  index: number;
  changeKind: PartNodeChangeKind;
  testId: string;
}): JSX.Element {
  const palette: Record<PartNodeChangeKind, { bg: string; fg: string }> = {
    added: { bg: '#10B981', fg: '#FFFFFF' },
    updated: { bg: '#F59E0B', fg: '#FFFFFF' },
    removed: { bg: '#F43F5E', fg: '#FFFFFF' },
    unchanged: { bg: '#6B6760', fg: '#FFFFFF' },
  };
  const { bg, fg } = palette[changeKind];
  return (
    <span
      data-testid={testId}
      data-change-index={index}
      aria-label={`Change ${index}`}
      style={{
        display: 'inline-block',
        minWidth: 14,
        height: 14,
        padding: '0 3px',
        marginLeft: 4,
        marginRight: 4,
        borderRadius: 7,
        background: bg,
        color: fg,
        fontSize: 9,
        fontWeight: 700,
        lineHeight: '14px',
        textAlign: 'center',
        verticalAlign: 'middle',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {index}
    </span>
  );
}

function PortRow({
  port,
  top,
  side,
}: {
  port: PortRef;
  top: number;
  side: 'left' | 'right';
}): JSX.Element {
  const isLeft = side === 'left';
  const ck = portChangeKindOf(port);
  const ckStyle = CHANGE_KIND_STYLES[ck];
  const labelStyle: CSSProperties = {
    [isLeft ? 'left' : 'right']: 14,
    top: top - 6,
    textAlign: isLeft ? 'left' : 'right',
    color: ck === 'unchanged' ? undefined : ckStyle.border,
    fontWeight: ck === 'unchanged' ? undefined : 600,
    ...(ckStyle.textDecoration !== undefined
      ? { textDecoration: ckStyle.textDecoration }
      : {}),
    ...(ckStyle.opacity !== undefined ? { opacity: ckStyle.opacity } : {}),
  };
  const handleStyle: CSSProperties = {
    top,
    width: 9,
    height: 9,
    background: ck === 'unchanged' ? 'var(--tessa-bg)' : ckStyle.fill,
    border: `1.2px solid ${ck === 'unchanged' ? 'var(--tessa-border)' : ckStyle.border}`,
    borderRadius: 0,
  };
  const hiddenStyle: CSSProperties = {
    top,
    width: 9,
    height: 9,
    background: 'transparent',
    border: 'none',
    opacity: 0,
    pointerEvents: 'none',
  };
  const portIdx = changeIndexOf(port);
  return (
    <>
      <span
        className="port-label"
        data-change-kind={ck}
        data-testid={`part-port-${port.name}`}
        style={labelStyle}
      >
        {isLeft && portIdx !== undefined ? (
          <PortChangeIndexChip
            index={portIdx}
            changeKind={ck}
            testId={`part-port-badge-${port.name}`}
          />
        ) : null}
        {port.name}
        {!isLeft && portIdx !== undefined ? (
          <PortChangeIndexChip
            index={portIdx}
            changeKind={ck}
            testId={`part-port-badge-${port.name}`}
          />
        ) : null}
      </span>
      <Handle
        type={isLeft ? 'target' : 'source'}
        position={isLeft ? Position.Left : Position.Right}
        id={isLeft ? `${port.name}.t` : `${port.name}.s`}
        style={handleStyle}
      />
      <Handle
        type={isLeft ? 'source' : 'target'}
        position={isLeft ? Position.Left : Position.Right}
        id={isLeft ? `${port.name}.s` : `${port.name}.t`}
        style={hiddenStyle}
      />
    </>
  );
}

/** Resolve the SysML v2 stereotype keyword, preferring `kind` over the legacy
 * `category` alias. Default to `part def` (SysML v2) — never `block`. */
function resolveKindLabel(data: PartNodeData): string {
  if (typeof data.kind === 'string' && data.kind.length > 0) return data.kind;
  if (typeof data.category === 'string' && data.category.length > 0) return data.category;
  return 'part def';
}

function ContainerBody({ data }: { data: PartNodeData }): JSX.Element {
  const { name, portsIn = [], portsOut = [], package: pkgName } = data;
  const kindLabel = resolveKindLabel(data);
  const portsTopOffset = HEADER_H + 12;
  const ck = resolveChangeKind(data);
  const ckStyle = CHANGE_KIND_STYLES[ck];
  const isDiffStyled = ck !== 'unchanged';
  const containerChangeIndex = changeIndexOf(data);

  return (
    <div
      data-kind="part-container"
      data-change-kind={ck}
      data-emphasis-kind={resolveEmphasisKind(data) ?? undefined}
      style={{
        width: '100%',
        height: '100%',
        background: isDiffStyled ? ckStyle.fill : 'var(--tessa-bg)',
        border: `1.5px solid ${isDiffStyled ? ckStyle.border : 'var(--tessa-border)'}`,
        borderStyle: ck === 'removed' ? 'dashed' : 'solid',
        borderRadius: 12,
        boxShadow: isDiffStyled
          ? 'none'
          : '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
        position: 'relative',
        color: 'var(--tessa-fg)',
        ...(ckStyle.opacity !== undefined ? { opacity: ckStyle.opacity } : {}),
        ...emphasisStyle(data),
      }}
    >
      {containerChangeIndex !== undefined ? (
        <ChangeIndexBadge
          index={containerChangeIndex}
          changeKind={ck}
          testId={`part-node-badge-${name ?? data.label ?? containerChangeIndex}`}
        />
      ) : null}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '9px 14px',
          background: 'transparent',
          borderBottom: `1px solid ${isDiffStyled ? ckStyle.border : 'var(--tessa-border-soft)'}`,
          borderTopLeftRadius: 11,
          borderTopRightRadius: 11,
          ...(ckStyle.textDecoration !== undefined
            ? { textDecoration: ckStyle.textDecoration }
            : {}),
        }}
      >
        <AccentDiamond color={isDiffStyled ? ckStyle.border : 'var(--tessa-accent, #2563EB)'} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 13,
              color: 'var(--tessa-fg)',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name ?? data.label ?? ''}
          </div>
          <div
            style={{
              fontStyle: 'italic',
              fontSize: 9.5,
              color: 'var(--tessa-muted)',
              lineHeight: 1.15,
            }}
          >
            «{kindLabel}»
            {pkgName !== undefined && pkgName !== '' ? ` · ${pkgName}` : ''}
          </div>
        </div>
      </div>

      {portsIn.map((p, i) => (
        <PortRow key={`in-${p.name}`} port={p} top={portsTopOffset + i * PORT_GAP} side="left" />
      ))}
      {portsOut.map((p, i) => (
        <PortRow key={`out-${p.name}`} port={p} top={portsTopOffset + i * PORT_GAP} side="right" />
      ))}
    </div>
  );
}

function LeafBody({ data }: { data: PartNodeData }): JSX.Element {
  const {
    name,
    attributes = [],
    portsIn = [],
    portsOut = [],
    package: pkgName,
  } = data;
  const kindLabel = resolveKindLabel(data);

  const attrBlockH = attributes.length > 0 ? attributes.length * ATTR_H + 8 : 0;
  const portCount = Math.max(portsIn.length, portsOut.length);
  const portsBlockH = portCount * PORT_GAP + PORT_TOP * 2;
  const portsTopOffset = HEADER_H + attrBlockH + PORT_TOP;

  const ck = resolveChangeKind(data);
  const ckStyle = CHANGE_KIND_STYLES[ck];
  const isDiffStyled = ck !== 'unchanged';
  const leafChangeIndex = changeIndexOf(data);
  const LeafIcon = iconForLeaf(name ?? data.label, kindLabel);

  return (
    <div
      data-kind="part-leaf"
      data-change-kind={ck}
      data-emphasis-kind={resolveEmphasisKind(data) ?? undefined}
      style={{
        background: isDiffStyled ? ckStyle.fill : 'var(--tessa-bg-elev)',
        border: `1px solid ${isDiffStyled ? ckStyle.border : 'var(--tessa-border-soft)'}`,
        borderStyle: ck === 'removed' ? 'dashed' : 'solid',
        borderRadius: 8,
        width: LEAF_W,
        fontSize: 11,
        color: 'var(--tessa-fg)',
        position: 'relative',
        paddingBottom: portsBlockH,
        boxShadow: isDiffStyled ? 'none' : '0 1px 1px rgba(15, 23, 42, 0.04)',
        ...(ckStyle.opacity !== undefined ? { opacity: ckStyle.opacity } : {}),
        ...emphasisStyle(data),
      }}
    >
      {leafChangeIndex !== undefined ? (
        <ChangeIndexBadge
          index={leafChangeIndex}
          changeKind={ck}
          testId={`part-node-badge-${name ?? data.label ?? leafChangeIndex}`}
        />
      ) : null}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '7px 10px',
          borderBottom: `1px solid ${isDiffStyled ? ckStyle.border : 'var(--tessa-border-soft)'}`,
          background: 'transparent',
          minHeight: HEADER_H - 12,
          boxSizing: 'border-box',
          ...(ckStyle.textDecoration !== undefined
            ? { textDecoration: ckStyle.textDecoration }
            : {}),
        }}
      >
        <LeafIcon
          size={15}
          strokeWidth={1.75}
          color={isDiffStyled ? ckStyle.border : 'var(--tessa-muted)'}
          style={{ flexShrink: 0 }}
          aria-hidden="true"
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 12,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name ?? data.label ?? ''}
          </div>
          <div
            style={{
              fontStyle: 'italic',
              fontSize: 9,
              color: 'var(--tessa-muted)',
              lineHeight: 1.1,
            }}
          >
            «{kindLabel}»
            {pkgName !== undefined && pkgName !== '' ? ` · ${pkgName}` : ''}
          </div>
        </div>
      </div>

      {attributes.length > 0 && (
        <div
          style={{
            padding: '4px 10px',
            borderBottom: '1px solid var(--tessa-border-soft)',
          }}
        >
          {attributes.map((a, i) => (
            <div
              key={`${a.name}-${i}`}
              style={{
                lineHeight: 1.4,
                fontSize: 10,
                color: 'var(--tessa-fg)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <span>{a.name}</span>
              {a.type !== undefined && (
                <span style={{ color: 'var(--tessa-muted)' }}>: {a.type}</span>
              )}
              {a.value !== undefined && (
                <span style={{ color: 'var(--tessa-muted)' }}> = {a.value}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {portsIn.map((p, i) => (
        <PortRow key={`in-${p.name}`} port={p} top={portsTopOffset + i * PORT_GAP} side="left" />
      ))}
      {portsOut.map((p, i) => (
        <PortRow key={`out-${p.name}`} port={p} top={portsTopOffset + i * PORT_GAP} side="right" />
      ))}
    </div>
  );
}

export function PartNode({ data }: NodeProps): JSX.Element {
  const partData = (data ?? {}) as PartNodeData;
  return partData.isContainer === true ? <ContainerBody data={partData} /> : <LeafBody data={partData} />;
}

export default PartNode;
